// ─── Mehrseitiger PDF-Druck des Stammbaums ──────────────────────────────────
//
// Der ganze Baum wird (unabhängig von Zoom/Pan) in ein Raster aus DIN-A4-
// Seiten (Querformat) zerlegt. Jede Kachel bekommt einen eigenen viewBox-
// Ausschnitt desselben SVGs. Ein neues Fenster öffnet die Druckvorschau –
// der Nutzer wählt dort „Als PDF speichern".

import { buildTreeSvgString } from './exportTreeSvg';

// Bedruckbare Fläche einer A4-Seite im Querformat bei 8 mm Rand (mm).
const PAGE_W_MM = 297 - 16;
const PAGE_H_MM = 210 - 16;
const PAGE_RATIO = PAGE_W_MM / PAGE_H_MM;

// Ziel-Kachelbreite in SVG-Einheiten (≈ 7 Knoten à 160 px pro Seite) –
// bestimmt, wie „hineingezoomt" der Ausdruck wird.
const TARGET_TILE_W = 1120;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
}

/**
 * Öffnet ein Druckfenster mit dem in A4-Seiten gekachelten Baum.
 * @returns Anzahl der erzeugten Seiten (0 = fehlgeschlagen/blockiert).
 */
export function printTreePdf(svgEl: SVGSVGElement, gEl: SVGGElement): number {
  const full = buildTreeSvgString(svgEl, gEl);

  const parsed = new DOMParser().parseFromString(full, 'image/svg+xml');
  const svg = parsed.documentElement;
  const viewBox = svg.getAttribute('viewBox');
  if (!viewBox) return 0;
  const [minX, minY, width, height] = viewBox.split(/\s+/).map(Number);
  const inner = svg.innerHTML;
  const fontFamily = svg.getAttribute('font-family') ?? 'Inter, system-ui, sans-serif';

  // Rasterung: Spalten aus Zielbreite, Zeilen aus dem Seitenverhältnis.
  // Anschließend gleichmäßig aufteilen, damit keine fast leere Randseite entsteht.
  const cols = Math.max(1, Math.ceil(width / TARGET_TILE_W));
  const rows = Math.max(1, Math.ceil(height / (TARGET_TILE_W / PAGE_RATIO)));
  const tileW = width / cols;
  const tileH = height / rows;

  const pages: string[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const vbX = minX + c * tileW;
      const vbY = minY + r * tileH;
      const pageNo = r * cols + c + 1;
      pages.push(`
        <div class="page">
          <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
               viewBox="${vbX} ${vbY} ${tileW} ${tileH}"
               preserveAspectRatio="xMidYMid meet"
               font-family="${escapeHtml(fontFamily)}">
            ${inner}
          </svg>
          <div class="pageinfo">Seite ${pageNo} / ${rows * cols} &nbsp;·&nbsp; Spalte ${c + 1}, Zeile ${r + 1}</div>
        </div>`);
    }
  }

  const html = `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="utf-8">
<title>Stammbaum – Druck</title>
<style>
  @page { size: A4 landscape; margin: 8mm; }
  html, body { margin: 0; padding: 0; background: #fff; }
  .page {
    position: relative;
    width: ${PAGE_W_MM}mm;
    height: ${PAGE_H_MM}mm;
    box-sizing: border-box;
    page-break-after: always;
    overflow: hidden;
    background: #fff;
  }
  .page:last-child { page-break-after: auto; }
  .page > svg { width: 100%; height: 100%; display: block; }
  .pageinfo {
    position: absolute; bottom: 1mm; right: 2mm;
    font: 9px ${escapeHtml(fontFamily)}; color: #94a3b8;
  }
  @media screen {
    body { background: #e2e8f0; padding: 12px; }
    .page { margin: 0 auto 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
  }
</style>
</head>
<body>
  ${pages.join('\n')}
  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.focus(); window.print(); }, 150);
    });
  </script>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) return 0; // Popup blockiert
  win.document.open();
  win.document.write(html);
  win.document.close();
  return rows * cols;
}
